'use client';

import { useState, useEffect } from 'react';
import Logo from '@/UIComponents/Logo/Logo';
import InputField from '@/UIlibrary/InputField/InputField';
import Button from '@/UIlibrary/Button/Button';
import { useTranslations } from 'use-intl';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { BRAND_COLORS } from '@/UIlibrary/globalTheme';
import { toast } from 'react-hot-toast';
import { Link, useParams } from 'react-router-dom';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import LocationRequest from '@/pages/Login/LocationRequest';
import { SCHEMAS } from '@/apolloClient';
import {
	useRecoverPasswordMutation,
	RecoverPasswordMutation,
	RecoverPasswordInput
} from '@/graphql/__generated__/authentication';
import logger from '@/logger';

const RecoverPasswordForm: React.FC = () => {
	const { lang } = useParams();
	const [formData, setFormData] = useState({
		email: ''
	});
	const [coordinates, setCoordinates] = useState<{ latitude: number; longitude: number } | null>(
		null
	);
	const [error, setError] = useState<string | null>(null);
	const [formIsValid, setFormIsValid] = useState<boolean>(false);
	const [emailSent, setEmailSent] = useState<boolean>(false);
	const t = useTranslations('RecoverPassword');

	const authenticationClient = useApolloClient(SCHEMAS.AUTHENTICATION);
	const [recoverPassword, { loading: loadingRecoverPassword }] = useRecoverPasswordMutation({
		client: authenticationClient,
		onCompleted: (data: RecoverPasswordMutation) => {
			const recoverData = data.recoverPassword;
			if (recoverData?.successful) {
				setEmailSent(true);
				toast.success(t('RecoverPasswordSuccess'));
			}
			if (recoverData?.mutationErrors && recoverData.mutationErrors.length > 0) {
				recoverData.mutationErrors.forEach((error) => {
					if (error) {
						switch (error.__typename) {
							case 'InvalidInput':
								if (error.field === 'email') {
									setError(error.message);
								}
								break;
							case 'ActionFailed':
							case 'InvalidAction':
								setError(error.message);
								break;
							default:
								logger.debug('Unhandled mutation error in recover password', error);
								break;
						}
					}
				});
			}
		},
		onError: (error) => {
			// Get the GraphQL error or fallback to default
			const graphQLError = error.graphQLErrors?.[0];
			let errorMessage = graphQLError?.message || t('RecoverPasswordFailed');

			// Check if this is a TOO_MANY_REQUESTS error with wait time
			if (
				graphQLError?.extensions?.error_code === 'TOO_MANY_REQUESTS' &&
				graphQLError?.extensions?.wait_time_ms
			) {
				// Convert milliseconds to seconds/minutes for better readability
				const waitTimeMs = Number(graphQLError.extensions.wait_time_ms);
				const waitTimeSeconds = Math.ceil(waitTimeMs / 1000);
				const minutes = Math.floor(waitTimeSeconds / 60);
				const seconds = waitTimeSeconds % 60;

				// Add the wait time to the error message
				if (minutes > 0) {
					errorMessage += ` (${minutes}m ${seconds}s)`;
				} else {
					errorMessage += ` (${seconds}s)`;
				}
			}

			toast.error(errorMessage, { duration: 3500 });

			// Log the error for debugging
			logger.debug('Error in recover password mutation:', error);
		}
	});

	const handleLocationReceived = (location: { latitude: number; longitude: number }) => {
		setCoordinates(location);
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError(null);
		const recoverPasswordInput: RecoverPasswordInput = {
			email: formData.email,
			latitude: String(coordinates?.latitude ?? 0),
			longitude: String(coordinates?.longitude ?? 0)
		};
		await recoverPassword({
			variables: {
				input: recoverPasswordInput
			}
		});
	};

	useEffect(() => {
		const noEmptyFields = formData.email.length > 0;
		setFormIsValid(noEmptyFields);
	}, [formData]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prevFormData) => ({
			...prevFormData,
			[name]: value
		}));
	};

	return (
		<>
			<LocationRequest onLocationReceived={handleLocationReceived} />
			<div className="w-full h-screen flex flex-col justify-center items-center">
				<form
					className="flex flex-col w-11/12 md:w-full max-w-[450px] mx-auto bg-[#FFFFFF] py-12 px-11 rounded-[25px] gap-6"
					onSubmit={handleSubmit}
				>
					<div className="flex flex-row justify-center">
						<Logo src="/logo-white.jpg" width={300} height={300} />
					</div>
					<div className="flex flex-col gap-8">
						<div className="flex flex-col gap-3">
							<Typography size="sm" type="display" weight="semibold">
								{t('RecoverPassword')}
							</Typography>
						</div>
						<div className="flex flex-col gap-2">
							{emailSent ? (
								<Typography size="md" color="subtitle">
									{t('RecoverPasswordSuccess')}
								</Typography>
							) : (
								<>
									<InputField
										name="email"
										type="email"
										label={t('SendEmail')}
										value={formData.email}
										onChange={handleChange}
										loading={loadingRecoverPassword}
										size={'auto'}
										placeholder={t('EnterEmail')}
										hint={error ? error : ''}
										error={!!error && error == t('PasswordFormatDisclaimer')}
										required={true}
									/>
									<Button
										type="submit"
										disabled={!formIsValid || loadingRecoverPassword}
										label={t('RecoveryLink')}
										size="auto"
									/>
								</>
							)}
						</div>
						<div className="flex items-center justify-center p-2 gap-1 ">
							<Typography size="sm" color="subtitle">
								{t('GoBack')}{' '}
							</Typography>
							<Link to={`/${lang}/signin`}>
								<Typography size="sm" weight="semibold" color={BRAND_COLORS.brand700}>
									{t('Login')}
								</Typography>
							</Link>
						</div>
					</div>
				</form>
			</div>
		</>
	);
};

export default RecoverPasswordForm;
